/**
 * Plan → Cook Progress — pure stage/copy helpers.
 * Spec: PLAN_WEEK_COOK_PROGRESS_FEATURE.md (Phase I).
 * Streak chip / streak-aware whisper deferred to a follow-up.
 */

import {
  MEAL_PLAN_DAY_ORDER,
  localTodayDate,
  parseLocalDateOnly,
  todayKeyInPlanWeek,
} from './mealPlanHelpers.js'

/** Phase I default: plan goal === cook goal. Keep parameterized in all copy. */
export const PLAN_COOK_GOAL_N = 3

const PLAN_COOK_DAY_LONG = Object.freeze({
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
  SUN: 'Sunday',
})

/**
 * Count planned + cooked from assigned planner meals.
 * Locked: skipped never counts; cooked ⊆ planned.
 * @param {unknown} assigned
 * @returns {{ planned: number, cooked: number }}
 */
export function countPlanCookProgress(assigned) {
  const meals = Array.isArray(assigned) ? assigned : []
  let planned = 0
  let cooked = 0
  for (const meal of meals) {
    if (!meal?.recipeId) continue
    const status = typeof meal.status === 'string' ? meal.status : 'planned'
    if (status === 'skipped' || status === 'night_off') continue
    planned += 1
    if (status === 'cooked') cooked += 1
  }
  return { planned, cooked }
}

/**
 * Remaining nights = today through Sunday inclusive when today is in the week.
 * Future week → 7; past week → 0.
 * @param {string|null|undefined} weekStartIso
 */
export function remainingNightsInPlanWeek(weekStartIso) {
  if (!weekStartIso) return 0
  const todayKey = todayKeyInPlanWeek(weekStartIso)
  if (todayKey != null) {
    const idx = MEAL_PLAN_DAY_ORDER.indexOf(todayKey)
    return idx < 0 ? 0 : 7 - idx
  }
  const monday = parseLocalDateOnly(weekStartIso)
  const today = localTodayDate()
  if (today < monday) return 7
  return 0
}

/**
 * First partial week: account created Tue–Sun of this plan week → orientation.
 * Signed up Monday (or earlier) → full coaching.
 * @param {{ accountCreatedAt?: string|null, weekStartIso?: string|null }} args
 */
export function isPlanWeekOrientation({ accountCreatedAt, weekStartIso } = {}) {
  if (!accountCreatedAt || !weekStartIso) return false
  const monday = parseLocalDateOnly(weekStartIso)
  if (!monday || Number.isNaN(monday.getTime())) return false
  const created = new Date(accountCreatedAt)
  if (Number.isNaN(created.getTime())) return false
  const createdLocal = new Date(created.getFullYear(), created.getMonth(), created.getDate())
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  if (createdLocal <= monday) return false
  if (createdLocal > sunday) return false
  return true
}

/**
 * @param {{ planned: number, cooked: number, remainingNights: number, goalN?: number }} args
 * @returns {'no_plan'|'planning'|'ready_to_cook'|'cooking'|'short'|'success'|'beyond'}
 */
export function resolvePlanCookStage({
  planned,
  cooked,
  remainingNights,
  goalN = PLAN_COOK_GOAL_N,
}) {
  if (cooked > goalN) return 'beyond'
  if (cooked >= goalN) return 'success'
  const cookNeed = Math.max(0, goalN - cooked)
  const unreachable = remainingNights < cookNeed
  // Late week: cook goal unreachable — short if any plan/cook activity (not empty no_plan).
  if (unreachable && (cooked > 0 || planned > 0)) return 'short'
  if (cooked > 0) return 'cooking'
  if (planned >= goalN) return 'ready_to_cook'
  if (planned > 0) return 'planning'
  return 'no_plan'
}

/**
 * Mid-week nudge when one cooked dinner remains to hit goal.
 * Streak-aware copy deferred — Phase I ships the non-streak line only.
 */
export function whisperForPlanCook({ stage, cooked, goalN, remainingNights }) {
  if (stage !== 'cooking' && stage !== 'ready_to_cook') return null
  const need = goalN - cooked
  if (need !== 1 || remainingNights < 1) return null
  return 'One more cooked dinner and this week counts.'
}

/**
 * @returns {{
 *   primary: 'plan'|'cook',
 *   headline: string,
 *   detail: string,
 *   showMeters: boolean,
 *   flare: boolean,
 *   whisper: string|null,
 * }}
 */
export function messageForPlanCook({
  stage,
  planned,
  cooked,
  goalN = PLAN_COOK_GOAL_N,
  remainingNights,
  isOrientation = false,
}) {
  if (isOrientation) {
    return {
      primary: 'plan',
      headline: 'Welcome — no pressure this week',
      detail: cooked > 0
        ? `${cooked} cooked already — quietly counted. Full coaching starts next Monday.`
        : 'Add a dinner when you\u2019re ready. Full coaching starts next Monday.',
      showMeters: false,
      flare: false,
      whisper: null,
    }
  }

  const nightsLabel = `${remainingNights} night${remainingNights === 1 ? '' : 's'}`
  const whisper = whisperForPlanCook({ stage, cooked, goalN, remainingNights })
  const base = { showMeters: true }

  switch (stage) {
    case 'no_plan':
      return {
        ...base,
        primary: 'plan',
        flare: false,
        whisper: null,
        headline: 'Plan a few dinners, skip the scramble',
        detail: `Add ${goalN} dinners so weeknights need less deciding.`,
      }
    case 'planning':
      return {
        ...base,
        primary: 'plan',
        flare: false,
        whisper: null,
        headline: 'The week\u2019s coming together',
        detail: `${planned} of ${goalN} planned — already a bit less dinner drama.`,
      }
    case 'ready_to_cook':
      return {
        ...base,
        primary: 'cook',
        flare: false,
        whisper,
        headline: 'Deciding\u2019s done for the week',
        detail: 'Mark a dinner cooked as it happens — the plan already did the hard part.',
      }
    case 'cooking':
      return {
        ...base,
        primary: 'cook',
        flare: false,
        whisper,
        headline: 'Settling into the week',
        detail: `${cooked} of ${goalN} cooked from the plan · ${nightsLabel} left.`,
      }
    case 'short':
      return {
        ...base,
        primary: 'cook',
        flare: false,
        whisper: null,
        headline: 'This week, on its own terms',
        detail: `${cooked} of ${goalN} cooked — still less deciding than a scramble. No make-up needed.`,
      }
    case 'success':
      return {
        ...base,
        primary: 'cook',
        flare: true,
        whisper: null,
        headline: 'That\u2019s the loop',
        detail: `${cooked} of ${goalN} cooked — without the usual weeknight drama.`,
      }
    case 'beyond':
      return {
        ...base,
        primary: 'cook',
        flare: true,
        whisper: null,
        headline: 'A full cooking week',
        detail: `${cooked} cooked this week. Keep going if you want.`,
      }
    default:
      return {
        ...base,
        primary: 'plan',
        flare: false,
        whisper: null,
        headline: '',
        detail: '',
      }
  }
}

/** Long day label for the progress eyebrow (e.g. "Wednesday"). */
export function planCookDayLabel(weekStartIso) {
  const key = todayKeyInPlanWeek(weekStartIso)
  if (!key) return null
  return PLAN_COOK_DAY_LONG[key] || null
}
